/**
 * Error boundary specifically for React Query errors
 * Provides better error handling and recovery for data fetching failures
 */

import { Component, type ReactNode } from "react"

import { ErrorReportDialog } from "@/components/error-report-dialog"
import { AlertTriangle, RefreshCw } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { logger } from "@/lib/logger"
import { reportClientError } from "@/lib/telemetry"

interface QueryErrorBoundaryProps {
  children: ReactNode
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface QueryErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class QueryErrorBoundary extends Component<
  QueryErrorBoundaryProps,
  QueryErrorBoundaryState
> {
  constructor(props: QueryErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): QueryErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error for debugging
    logger.error("QueryErrorBoundary caught an error:", {
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name,
      },
      errorInfo,
    })

    // Additive telemetry — dedups with any global / react-query emission of the
    // same error. Never throws.
    reportClientError(error, { source: "query-error-boundary" })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    const { hasError, error } = this.state
    const { fallback, children } = this.props

    if (hasError && error) {
      if (fallback) {
        return fallback(error, this.handleReset)
      }

      return (
        <div className="flex min-h-[200px] items-center justify-center p-4">
          <Card className="border-destructive/50 w-full max-w-md">
            <CardHeader>
              <div className="mb-4 flex justify-center">
                <AlertTriangle className="text-destructive h-12 w-12" />
              </div>
              <CardTitle className="text-destructive text-xl">Data Loading Error</CardTitle>
              <CardDescription className="pt-2">
                Failed to load data. This might be a temporary issue.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-2 text-sm">
              <p className="italic">{error.message || "An unknown error occurred"}</p>
            </CardContent>
            <CardFooter className="flex flex-wrap justify-center gap-2">
              <Button variant="destructive" onClick={this.handleReset}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
              <ErrorReportDialog />
            </CardFooter>
          </Card>
        </div>
      )
    }

    return children
  }
}
